/**
 * GET /api/training_preview_stats – validácia HTTP/body kontraktu.
 * getRequest pri 4xx/5xx nemusí thrownúť; môže vrátiť body so statusom.
 */

import { uiT } from './uiTranslate';

export const TRAINING_PREVIEW_LOAD_FAILED_KEY = 'PreviewTraining.LoadFailed';
export const TRAINING_PREVIEW_INVALID_RANGE_KEY = 'PreviewTraining.InvalidRange';

const UNSAFE_ERROR_RE = /sqlstate|\bsql\b|exception|stack trace|select |insert |update |delete |from |where |join |\.php\b|\/var\/|\/home\/|[a-z]:\\|\by:\\|\bw:\\|steelcon|at\s+\S+\s+\(/i;

function toValidDate(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d;
}

function parseCountArray(value, length) {
  if (!Array.isArray(value) || value.length !== length) return null;
  const nums = value.map((item) => Number(item));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return null;
  return nums;
}

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function looksLikePreviewStatsPayload(value) {
  return isPlainObject(value) && (
    Array.isArray(value.employee)
    || Array.isArray(value.company)
    || Array.isArray(value.invites)
    || Object.prototype.hasOwnProperty.call(value, 'periodStats')
  );
}

function unwrapPreviewStatsPayload(res) {
  if (!isPlainObject(res)) return null;
  if (looksLikePreviewStatsPayload(res.data) && !looksLikePreviewStatsPayload(res)) {
    return res.data;
  }
  if (looksLikePreviewStatsPayload(res.data) && Number(res.status) === 200) {
    return res.data;
  }
  return res;
}

export function isUnsafeErrorText(text) {
  if (typeof text !== 'string' || text.trim() === '') return true;
  return UNSAFE_ERROR_RE.test(text);
}

export function resolveTrainingPreviewErrorKey(error) {
  if (error?.kind === 'invalid_range' || error?.kind === 'reversed_range') {
    return TRAINING_PREVIEW_INVALID_RANGE_KEY;
  }
  return TRAINING_PREVIEW_LOAD_FAILED_KEY;
}

export function resolveTrainingPreviewErrorMessage(error, t) {
  const key = resolveTrainingPreviewErrorKey(error);
  const fallback = key === TRAINING_PREVIEW_INVALID_RANGE_KEY
    ? uiT(TRAINING_PREVIEW_INVALID_RANGE_KEY)
    : (typeof t === 'function' ? t('Common.LoadFailed') : uiT('Common.LoadFailed'));
  const translated = typeof t === 'function' ? t(key) : fallback;
  if (typeof translated !== 'string' || translated === key || isUnsafeErrorText(translated)) {
    return fallback;
  }
  return translated;
}

export function validateTrainingPreviewDateRange(dateFrom, dateTo) {
  if (dateFrom == null && dateTo == null) return { ok: true };
  if (dateFrom == null || dateTo == null) {
    return { ok: false, error: { kind: 'invalid_range' } };
  }
  const from = toValidDate(dateFrom);
  const to = toValidDate(dateTo);
  if (!from || !to) {
    return { ok: false, error: { kind: 'invalid_range' } };
  }
  if (from.getTime() > to.getTime()) {
    return { ok: false, error: { kind: 'reversed_range' } };
  }
  return { ok: true, dateFrom: from, dateTo: to };
}

export function parseTrainingPreviewStatsPayload(payload) {
  if (!isPlainObject(payload)) return null;
  const employee = parseCountArray(payload.employee, 4);
  const company = parseCountArray(payload.company, 4);
  const invites = parseCountArray(payload.invites, 3);
  if (!employee || !company || !invites) return null;
  if (payload.periodStats != null && !isPlainObject(payload.periodStats)) return null;
  return {
    employee,
    company,
    invites,
    periodStats: payload.periodStats ?? null,
  };
}

export function parseTrainingPreviewStatsResponse(res) {
  if (!isPlainObject(res)) {
    return { ok: false, error: { kind: 'malformed', status: null } };
  }
  const status = Number(res.status);
  const hasStatus = Number.isFinite(status);
  if (hasStatus && status !== 200) {
    const kind = (status === 400 || status === 422) ? 'invalid_range' : 'load_failed';
    return { ok: false, error: { kind, status } };
  }
  const payload = unwrapPreviewStatsPayload(res);
  const data = parseTrainingPreviewStatsPayload(payload);
  if (!data) {
    return { ok: false, error: { kind: 'malformed', status: hasStatus ? status : 200 } };
  }
  return { ok: true, data };
}

/** „Bez záznamov“ len po úspešnom 200 s validným payloadom. */
export function shouldShowTrainingPreviewEmptyRecords(error, stats) {
  return error == null && stats != null;
}

/** Nový stav po fetchi / validácii. Neúspech nikdy neprepíše last stats nulami. */
export function nextTrainingPreviewStatsState(prev, event) {
  const previous = prev && typeof prev === 'object'
    ? prev
    : { stats: null, error: null };
  if (event?.rejected) {
    return { stats: previous.stats, error: { kind: 'network' } };
  }
  if (event && Object.prototype.hasOwnProperty.call(event, 'range')) {
    const range = event.range;
    const validated = validateTrainingPreviewDateRange(range?.dateFrom, range?.dateTo);
    if (!validated.ok) {
      return { stats: previous.stats, error: validated.error };
    }
  }
  if (event && Object.prototype.hasOwnProperty.call(event, 'response')) {
    const parsed = parseTrainingPreviewStatsResponse(event.response);
    if (!parsed.ok) {
      return { stats: previous.stats, error: parsed.error };
    }
    return { stats: parsed.data, error: null };
  }
  return previous;
}
